import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Clock, Loader2, Lock } from "lucide-react";
import { Eyebrow } from "@/components/site/Primitives";
import { useLink, usePage, useT } from "@/lib/i18n";
import { api, errCode } from "@/lib/api";

const inputCls = "mt-2 w-full rounded-2xl border border-forest/20 bg-white/80 px-5 py-4 text-base text-forest outline-none transition-colors focus:border-gold-deep";

const Option = ({ type, name, checked, onChange, label, testid }) => (
  <label
    data-testid={testid}
    className={`flex min-h-[64px] cursor-pointer items-center gap-4 rounded-2xl border px-5 py-4 text-[15px] transition-[background-color,border-color] duration-300 sm:text-base ${checked ? "border-forest bg-forest text-ivory" : "border-forest/20 bg-white/60 text-forest hover:border-forest/50"}`}
  >
    <input type={type} name={name} checked={checked} onChange={onChange} className="sr-only" />
    <span className={`flex h-6 w-6 shrink-0 items-center justify-center border ${type === "radio" ? "rounded-full" : "rounded-md"} ${checked ? "border-gold-light bg-gold-light text-forest" : "border-forest/40"}`}>
      {checked && <Check size={14} strokeWidth={3} />}
    </span>
    {label}
  </label>
);

const Question = ({ title, hint, children }) => (
  <fieldset>
    <legend className="font-display text-3xl leading-tight text-forest sm:text-[2.4rem]">{title}</legend>
    {hint && <p className="mt-3 text-sm text-forest/70">{hint}</p>}
    <div className="mt-8 space-y-3">{children}</div>
  </fieldset>
);

const initial = { location: "", needs: [], needs_other: "", urgency: "", situation: "", willing_to_pay: "", full_name: "", email: "", phone: "", contact_method: "email", consent_privacy: false, website: "" };

export default function Apply() {
  const t = useT();
  const a = t.apply;
  const { lang } = usePage();
  const link = useLink();
  const navigate = useNavigate();
  const started = useRef(Date.now());
  const [step, setStep] = useState(0);
  const [v, setV] = useState(initial);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const set = (k, val) => { setV((p) => ({ ...p, [k]: val })); setError(""); };
  const toggleNeed = (n) => set("needs", v.needs.includes(n) ? v.needs.filter((x) => x !== n) : [...v.needs, n]);

  const validate = (s) => {
    const e = a.errors;
    if (s === 0 && !v.location) return e.location;
    if (s === 1 && !v.needs.length) return e.needs;
    if (s === 2 && !v.urgency) return e.urgency;
    if (s === 3 && v.situation.trim().length < 20) return e.situation;
    if (s === 4 && !v.willing_to_pay) return e.pay;
    if (s === 5) {
      if (v.full_name.trim().length < 2) return e.name;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) return e.email;
      if (v.contact_method !== "email" && !v.phone.trim()) return e.phone;
      if (!v.consent_privacy) return e.consent;
    }
    return "";
  };

  const next = () => {
    const msg = validate(step);
    if (msg) return setError(msg);
    setStep((s) => s + 1);
    window.__lenis?.scrollTo(0);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const msg = validate(5);
    if (msg) return setError(msg);
    setSending(true);
    try {
      await api.post("/applications", { ...v, email: v.email.trim(), lang, started_at: started.current });
      navigate(link("thanks"));
    } catch (err) {
      const code = errCode(err);
      setError(code === "rate_limited" ? a.errors.rate : code === "too_fast" ? a.errors.fast : a.errors.generic);
      setSending(false);
    }
  };

  const steps = [
    <Question key="0" title={a.q1}>
      {["planning", "living"].map((k) => <Option key={k} type="radio" name="location" checked={v.location === k} onChange={() => set("location", k)} label={a.location[k]} testid={`apply-location-${k}`} />)}
    </Question>,
    <Question key="1" title={a.q2} hint={a.q2hint}>
      <div className="grid gap-3 sm:grid-cols-2">
        {Object.keys(a.needs).map((k) => <Option key={k} type="checkbox" name="needs" checked={v.needs.includes(k)} onChange={() => toggleNeed(k)} label={a.needs[k]} testid={`apply-need-${k}`} />)}
      </div>
      {v.needs.includes("other") && (
        <label className="block pt-2 text-sm font-medium text-forest">{a.needsOther}
          <input data-testid="apply-need-other-input" maxLength={300} className={inputCls} value={v.needs_other} onChange={(e) => set("needs_other", e.target.value)} />
        </label>
      )}
    </Question>,
    <Question key="2" title={a.q3}>
      {Object.keys(a.urgency).map((k) => <Option key={k} type="radio" name="urgency" checked={v.urgency === k} onChange={() => set("urgency", k)} label={a.urgency[k]} testid={`apply-urgency-${k}`} />)}
    </Question>,
    <Question key="3" title={a.q4} hint={a.q4hint}>
      <textarea data-testid="apply-situation-input" rows={7} maxLength={3000} className={inputCls} value={v.situation} onChange={(e) => set("situation", e.target.value)} aria-label={a.q4} />
      <p className="text-right text-xs text-forest/60">{v.situation.length} / 3000</p>
    </Question>,
    <Question key="4" title={a.q5}>
      {Object.keys(a.pay).map((k) => <Option key={k} type="radio" name="pay" checked={v.willing_to_pay === k} onChange={() => set("willing_to_pay", k)} label={a.pay[k]} testid={`apply-pay-${k}`} />)}
    </Question>,
    <Question key="5" title={a.q6}>
      <label className="block text-sm font-medium text-forest">{a.fullName}<input data-testid="apply-name-input" autoComplete="name" maxLength={120} className={inputCls} value={v.full_name} onChange={(e) => set("full_name", e.target.value)} /></label>
      <label className="block text-sm font-medium text-forest">{a.email}<input data-testid="apply-email-input" type="email" autoComplete="email" className={inputCls} value={v.email} onChange={(e) => set("email", e.target.value)} /></label>
      <label className="block text-sm font-medium text-forest">{a.phone}<input data-testid="apply-phone-input" type="tel" autoComplete="tel" maxLength={40} className={inputCls} value={v.phone} onChange={(e) => set("phone", e.target.value)} /></label>
      <p className="pt-2 text-sm font-medium text-forest">{a.method}</p>
      <div className="grid gap-3 sm:grid-cols-3">
        {Object.keys(a.methods).map((k) => <Option key={k} type="radio" name="method" checked={v.contact_method === k} onChange={() => set("contact_method", k)} label={a.methods[k]} testid={`apply-method-${k}`} />)}
      </div>
      <div className="mt-4 rounded-2xl bg-ivory-2 p-5 text-sm leading-relaxed text-forest/80" data-testid="apply-privacy-notice">
        <p className="mb-2 flex items-center gap-2 font-semibold text-forest"><Lock size={15} /> {a.privacyTitle}</p>
        <p>{a.privacyText} <Link to={link("privacy")} className="underline underline-offset-4" target="_blank">{a.privacyLink}</Link></p>
      </div>
      <label className="flex items-start gap-3 pt-2 text-sm leading-relaxed text-forest">
        <input data-testid="apply-consent-checkbox" type="checkbox" checked={v.consent_privacy} onChange={(e) => set("consent_privacy", e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[#17382E]" />
        {a.consent}
      </label>
    </Question>,
  ];

  const last = step === steps.length - 1;
  return (
    <section data-testid="apply-page" className="container-x pb-24 pt-32 sm:pt-40">
      <div className="grid gap-12 lg:grid-cols-12">
        <aside className="lg:col-span-4">
          <Eyebrow>{a.eyebrow}</Eyebrow>
          <h1 className="mt-5 font-display text-4xl leading-[1.02] text-forest sm:text-5xl">{a.title}</h1>
          <p className="mt-5 text-[15px] leading-relaxed text-forest/75">{a.intro}</p>
          <p className="mt-5 flex items-center gap-2 text-sm text-gold-text"><Clock size={16} /> {a.time}</p>
          <ol className="mt-10 hidden space-y-1 lg:block">
            {a.steps.map((s, i) => (
              <li key={s} className={`flex items-center gap-4 rounded-xl px-3 py-2.5 text-sm transition-colors ${i === step ? "bg-forest text-ivory" : i < step ? "text-forest" : "text-forest/60"}`}>
                <span className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs ${i < step ? "border-gold-deep bg-gold-deep text-white" : "border-current"}`}>{i < step ? <Check size={13} /> : i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
          <p className="mt-10 hidden text-xs leading-relaxed text-forest/70 lg:block">{t.discovery.note}</p>
        </aside>
        <form onSubmit={submit} noValidate className="card-soft p-6 sm:p-10 lg:col-span-8 lg:p-14" data-testid="apply-form">
          <input type="text" name="website" value={v.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
          <div className="mb-10">
            <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.18em] text-gold-text">
              <span data-testid="apply-step-indicator">{a.stepLabel} {step + 1} {a.of} {steps.length}</span>
              <span className="lg:hidden">{a.steps[step]}</span>
            </div>
            <div className="mt-3 h-1 overflow-hidden rounded-full bg-forest/10">
              <motion.div className="h-full rounded-full bg-gold-deep" animate={{ width: `${((step + 1) / steps.length) * 100}%` }} transition={{ duration: 0.5 }} />
            </div>
          </div>
          <motion.div key={step} initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }}>
            {steps[step]}
          </motion.div>
          {error && <p role="alert" data-testid="apply-error" className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>}
          <div className="mt-10 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
            {step > 0 ? (
              <button type="button" onClick={() => { setError(""); setStep((s) => s - 1); }} data-testid="apply-back-btn" className="btn btn-outline"><ArrowLeft size={16} /> {a.back}</button>
            ) : <span />}
            {last ? (
              <button type="submit" disabled={sending} data-testid="apply-submit-btn" className="btn btn-gold">
                {sending ? <Loader2 size={16} className="animate-spin" /> : null} {sending ? a.submitting : a.submit}
              </button>
            ) : (
              <button type="button" onClick={next} data-testid="apply-next-btn" className="btn btn-forest">{a.next} <ArrowRight size={16} className="btn-arrow" /></button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
